import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo } from 'react';
import {
  SetAsAdminTicketingUserValidationSchema,
  TSetAsAdminTicketingUserValidation,
  useSetAsAdminTicketingUserMutation,
} from '../../api/set-as-admin-ticketing-user.api';
import { GET_LIST_TICKETING_USER_QUERY_KEY } from '../../api/get-list-ticketing-user.api';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

const TOAST_MESSAGES_SET_AS_ADMIN_USER_TICKETING = {
  SET_AS_ADMIN_USER_TICKETING_ERROR: 'Error set as admin user ticketing, please try again',
  SET_AS_ADMIN_USER_TICKETING_SUCCESS: 'User ticketing set as admin successfully!',
};

export type TSetAsAdminTicketingUserModalType = 'SetAsAdminTicketingUserModal';
export interface ISetAsAdminTicketingUserModalProps {
  userId: string;
}
const SetAsAdminTicketingUserModal = memo((props: ISetAsAdminTicketingUserModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const form = useForm<TSetAsAdminTicketingUserValidation>({
    resolver: zodResolver(SetAsAdminTicketingUserValidationSchema),
    mode: 'onChange',
  });

  const mutationSetAsAdminTicketingUser = useSetAsAdminTicketingUserMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error) ||
          TOAST_MESSAGES_SET_AS_ADMIN_USER_TICKETING.SET_AS_ADMIN_USER_TICKETING_ERROR;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_TICKETING_USER_QUERY_KEY] });
        modalActions.closeModal('SetAsAdminTicketingUserModal');
        showToast({
          message: TOAST_MESSAGES_SET_AS_ADMIN_USER_TICKETING.SET_AS_ADMIN_USER_TICKETING_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenSetAsAdminTicketingUserModal = useMemo(() => {
    return modalActions.checkIsOpenModal('SetAsAdminTicketingUserModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationSetAsAdminTicketingUser.isPending;
  }, [mutationSetAsAdminTicketingUser.isPending]);

  const isLoadingForm = useMemo(() => {
    return mutationSetAsAdminTicketingUser.isPending;
  }, [mutationSetAsAdminTicketingUser.isPending]);

  // EFFECTS
  useEffect(() => {
    if (props) {
      form.reset({
        userId: props?.userId || '',
      });
    }
  }, [props?.userId, form]);

  // HANDLERS
  const handleSubmitForm = useCallback(
    (data: TSetAsAdminTicketingUserValidation) => {
      mutationSetAsAdminTicketingUser.mutate({
        userId: data?.userId || '',
      });
    },
    [mutationSetAsAdminTicketingUser]
  );

  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('SetAsAdminTicketingUserModal');
  }, [modalActions]);

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  return (
    <Modal
      isOpen={isOpenSetAsAdminTicketingUserModal}
      lockBodyScroll
      onClose={handleClose}
      className="p-[32px] md:w-[500px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Set as Ticketing Admin</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitForm)(e);
          }}
          id="set-as-admin-user-ticketing-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">
            Update this member's role and access permissions
          </span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="set-as-admin-user-ticketing-form" type="submit" disabled={isDisabledForm}>
          {isLoadingForm ? <Spinner className="w-[18px] h-[18px]" /> : null} Set as Admin
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
SetAsAdminTicketingUserModal.displayName = 'SetAsAdminTicketingUserModal';

export default SetAsAdminTicketingUserModal;
